import { supabase } from '@/lib/supabase';
import { computeBalance, formatMoney, type CreditTransaction } from '@/lib/credit';
import { getBalance as getWalletBalance } from '@/lib/wallet';

// ─────────────────────────────────────────────────────────────
// Solde « magasin » (fonds déposés par un client chez un commerçant)
// et transfert de ce solde entre deux clients d'un même commerçant.
//
// Le solde magasin est scopé au commerçant : les fonds transférés
// restent utilisables UNIQUEMENT chez ce même commerce.
// ─────────────────────────────────────────────────────────────

export interface StoreBalance {
  /** Fonds disponibles en dépôt chez ce commerçant (>= 0). */
  store: number;
  /** Dette du client envers ce commerçant (>= 0). */
  debt: number;
}

/** Solde magasin + dette d'un client chez un commerçant donné. */
export async function getStoreBalance(merchantId: number, creditCustomerId: number): Promise<StoreBalance> {
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('type, amount')
    .eq('merchant_id', merchantId)
    .eq('customer_id', creditCustomerId);
  if (error) throw error;
  const net = computeBalance((data || []) as Pick<CreditTransaction, 'type' | 'amount'>[]);
  return { store: Math.max(0, -net), debt: Math.max(0, net) };
}

/** Retrouve l'id du carnet de crédit d'un client à partir de son compte Zifek. */
export async function resolveCreditCustomerId(
  merchantId: number,
  linkedUserId: number | null
): Promise<number | null> {
  if (linkedUserId == null) return null;
  const { data } = await supabase
    .from('credit_customers')
    .select('id')
    .eq('merchant_id', merchantId)
    .eq('linked_user_id', linkedUserId)
    .maybeSingle();
  return (data?.id as number) ?? null;
}

/** Solde du portefeuille Zifek (utilisable partout sur les sites Zifek). */
export async function getWalletBalance(userId: number | null): Promise<number> {
  if (userId == null) return 0;
  return getWalletBalance(userId);
}

// ─────────────────────────────────────────────────────────────
// Historique des transferts de solde magasin
// ─────────────────────────────────────────────────────────────

export interface StoreTransferMovement {
  id: number;
  type: 'transfer_in' | 'transfer_out';
  amount: number;
  description: string | null;
  created_at: string;
}

/**
 * Historique dédié des transferts de solde magasin d'un client chez un commerçant :
 * mouvements sortants (envoyés) et entrants (reçus).
 */
export async function getStoreTransferHistory(
  merchantId: number,
  creditCustomerId: number,
  limit = 50,
  /** Ne remonter que les mouvements des N derniers jours (7 / 30). 0 ou absent = tout. */
  days = 0
): Promise<StoreTransferMovement[]> {
  let query = supabase
    .from('credit_transactions')
    .select('id, type, amount, description, created_at')
    .eq('merchant_id', merchantId)
    .eq('customer_id', creditCustomerId)
    .in('type', ['transfer_in', 'transfer_out']);
  if (days && days > 0) {
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    query = query.gte('created_at', since);
  }
  const { data, error } = await query.order('created_at', { ascending: false }).limit(limit);
  if (error) return [];
  return (data || []) as StoreTransferMovement[];
}

// ─────────────────────────────────────────────────────────────
// Destinataires possibles d'un transfert (autres clients du commerce)
// ─────────────────────────────────────────────────────────────

export interface MerchantClient {
  id: number;
  full_name: string;
  linked_user_id: number | null;
  username?: string | null;
  email?: string | null;
}

/** Autres clients d'un commerçant, candidats à recevoir un transfert de solde. */
export async function listMerchantStoreClients(
  merchantId: number,
  excludeCustomerId?: number | null
): Promise<MerchantClient[]> {
  const { data, error } = await supabase
    .from('credit_customers')
    .select('id, full_name, linked_user_id, username, email')
    .eq('merchant_id', merchantId)
    .order('full_name', { ascending: true });
  if (error) return [];
  return ((data || []) as MerchantClient[]).filter((c) => c.id !== excludeCustomerId);
}

/**
 * Retrouve le client destinataire d'un transfert à partir de l'email ou du
 * nom d'utilisateur saisi par le client — SANS proposer de liste par défaut.
 * La recherche est strictement limitée aux clients de ce commerçant.
 */
export async function findMerchantStoreRecipient(
  merchantId: number,
  query: string,
  excludeCustomerId?: number | null
): Promise<MerchantClient | null> {
  const q = query.trim().replace(/[%,()*\\]/g, ' ').trim();
  if (!q) return null;
  const { data, error } = await supabase
    .from('credit_customers')
    .select('id, full_name, linked_user_id, username, email')
    .eq('merchant_id', merchantId)
    .or(`username.ilike.${q},email.ilike.${q},full_name.ilike.${q}`)
    .limit(5);
  if (error || !data) return null;
  const rows = (data as MerchantClient[]).filter((c) => c.id !== excludeCustomerId);
  return rows[0] ?? null;
}

// ─────────────────────────────────────────────────────────────
// Comptes « solde magasin » d'un client (côté /mon-compte)
// ─────────────────────────────────────────────────────────────

export interface ClientStoreAccount {
  creditCustomerId: number;
  merchantId: number;
  merchantName: string;
  merchantImage: string | null;
  merchantUsername: string | null;
  currency: string;
  store: number;
  debt: number;
}

/**
 * Liste tous les commerces où ce client détient un solde magasin
 * (c.-à-d. les commerçants chez qui son compte Zifek est rattaché à un carnet).
 */
export async function listClientStoreAccounts(clientUserId: number): Promise<ClientStoreAccount[]> {
  const { data: rows, error } = await supabase
    .from('credit_customers')
    .select('id, merchant_id')
    .eq('linked_user_id', clientUserId);
  if (error || !rows || rows.length === 0) return [];

  const accounts = await Promise.all(
    (rows as { id: number; merchant_id: number }[]).map(async (row) => {
      const [merchantRes, balance] = await Promise.all([
        supabase
          .from('users')
          .select('id, name, nomcommerce, image, user_name, monaie')
          .eq('id', row.merchant_id)
          .maybeSingle(),
        getStoreBalance(row.merchant_id, row.id).catch(() => ({ store: 0, debt: 0 })),
      ]);
      const m = merchantRes.data as {
        id: number;
        name: string | null;
        nomcommerce: string | null;
        image: string | null;
        user_name: string | null;
        monaie: string | null;
      } | null;
      const account: ClientStoreAccount = {
        creditCustomerId: row.id,
        merchantId: row.merchant_id,
        merchantName: m?.nomcommerce || m?.name || `Commerce #${row.merchant_id}`,
        merchantImage: m?.image || null,
        merchantUsername: m?.user_name || null,
        currency: m?.monaie || 'MAD',
        store: balance.store,
        debt: balance.debt,
      };
      return account;
    })
  );

  // Les soldes les plus élevés d'abord, puis par nom.
  return accounts.sort((a, b) => b.store - a.store || a.merchantName.localeCompare(b.merchantName));
}

// ─────────────────────────────────────────────────────────────
// Transfert de solde magasin (marchand ou client lui-même)
// ─────────────────────────────────────────────────────────────

/** Notifie le commerçant et le destinataire (best-effort, jamais bloquant). */
async function notifyStoreTransfer(input: {
  merchantId: number;
  fromCustomerId: number;
  toCustomerId: number;
  amount: number;
  currency: string;
  fromName: string;
  toName: string;
  actorId: number | null;
}): Promise<void> {
  const { merchantId, fromCustomerId, toCustomerId, amount, currency, fromName, toName, actorId } = input;
  const { data: links } = await supabase
    .from('credit_customers')
    .select('id, linked_user_id')
    .in('id', [fromCustomerId, toCustomerId]);
  const rows = (links || []) as { id: number; linked_user_id: number | null }[];
  const fromLinked = rows.find((l) => l.id === fromCustomerId)?.linked_user_id ?? null;
  const toLinked = rows.find((l) => l.id === toCustomerId)?.linked_user_id ?? null;

  const amountLabel = formatMoney(amount, currency);
  const notifications: Record<string, unknown>[] = [];

  // 1. Au commerçant
  notifications.push({
    user_id: merchantId,
    sender_id: actorId ?? fromLinked ?? merchantId,
    publication_id: 0,
    type: 'store_transfer',
    message: `Transfert de solde magasin : ${fromName} a envoyé ${amountLabel} à ${toName}`,
    is_read: 0,
  });

  // 2. Au destinataire
  if (toLinked != null) {
    notifications.push({
      user_id: toLinked,
      sender_id: actorId ?? merchantId,
      publication_id: 0,
      type: 'store_transfer',
      message: `${fromName} vous a envoyé ${amountLabel} de solde magasin`,
      is_read: 0,
    });
  }

  // 3. À l'émetteur (le client qui envoie) reçoit une confirmation
  if (fromLinked != null && fromLinked !== toLinked) {
    notifications.push({
      user_id: fromLinked,
      sender_id: actorId ?? merchantId,
      publication_id: 0,
      type: 'store_transfer',
      message: `Vous avez envoyé ${amountLabel} de solde magasin à ${toName}`,
      is_read: 0,
    });
  }

  try {
    await supabase.from('notifications').insert(notifications);
    // Prévient la cloche de notifications (même onglet) de se rafraîchir immédiatement.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('notifications-changed'));
    }
  } catch {
    /* notification best-effort */
  }
}

/**
 * Transfère une partie du solde magasin d'un client vers un autre client
 * du même commerçant. Les fonds ne sortent jamais de chez ce commerce :
 * ils sont débités du premier et déposés chez le second, dans le même carnet.
 *
 * Le commerçant et le destinataire sont notifiés à chaque transfert.
 */
export async function transferStoreBalance(input: {
  merchantId: number;
  fromCustomerId: number;
  toCustomerId: number;
  amount: number;
  fromName: string;
  toName: string;
  actorId: number | null;
  currency?: string;
}): Promise<{ success: boolean; error?: string }> {
  const { merchantId, fromCustomerId, toCustomerId, amount, fromName, toName, actorId, currency = 'MAD' } = input;
  const amt = Number(amount);
  if (!Number.isFinite(amt) || amt <= 0) return { success: false, error: 'Montant invalide.' };
  if (fromCustomerId === toCustomerId) return { success: false, error: 'Choisissez un autre client.' };

  const balance = await getStoreBalance(merchantId, fromCustomerId);
  if (balance.store < amt) {
    return { success: false, error: `Solde magasin insuffisant (disponible : ${formatMoney(balance.store, currency)}).` };
  }

  const base = { merchant_id: merchantId, amount: amt, created_by: actorId };

  const { error: outErr } = await supabase.from('credit_transactions').insert({
    ...base,
    customer_id: fromCustomerId,
    type: 'transfer_out',
    description: `Solde magasin transféré à ${toName}`,
  });
  if (outErr) return { success: false, error: outErr.message };

  const { error: inErr } = await supabase.from('credit_transactions').insert({
    ...base,
    customer_id: toCustomerId,
    type: 'transfer_in',
    description: `Solde magasin reçu de ${fromName}`,
  });
  if (inErr) {
    // Annule le débit émetteur pour éviter toute perte de fonds.
    await supabase.from('credit_transactions').insert({
      ...base,
      customer_id: fromCustomerId,
      type: 'adjustment',
      amount: -amt,
      description: 'Annulation transfert échoué',
    });
    return { success: false, error: "Le destinataire n'a pas pu être crédité." };
  }

  // Notifications au commerçant et au destinataire.
  await notifyStoreTransfer({
    merchantId,
    fromCustomerId,
    toCustomerId,
    amount: amt,
    currency,
    fromName,
    toName,
    actorId,
  });

  return { success: true };
}